import React, { useEffect, useRef, useState } from 'react';
import { X, Clock, Phone, User, Truck, CheckCircle2, XCircle, MapPin, Building2, Loader2 } from 'lucide-react';
import commandCenterService from '../../services/commandCenterService';
import { deriveTripStage } from './mockTrackingData';
import { DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC, getTimeoutConfig } from '../../services/configService';

const POLL_INTERVAL_MS = 1000;

// Real Auto Assign — full parity with the Hospital dashboard's Automatic
// Assignment: the backend ranks up to 5 nearby candidates, sends a real
// request to the nearest one, and this popup polls the booking's actual
// status. On a decline/timeout it calls advanceAutoAssign to move to the
// next candidate in that same server-side queue — entirely real requests,
// no client-side simulation. The "waiting" layout (timer pill, Ambulance
// Details / Driver Information / Vehicle Information / Allocation Status
// cards) mirrors Hospital's real AutomaticAssignmentPopup ("Contacting
// Driver") card-for-card, minus the Hospital field.
const CommandCenterAutoAssignPopup = ({ bookingId, filters, onClose, onConfirmed, onExhausted, onDone }) => {
  const [phase, setPhase] = useState('starting'); // 'starting' | 'waiting' | 'confirmed' | 'exhausted' | 'no-candidates' | 'error'
  const [attempt, setAttempt] = useState({ current: 1, total: 1 });
  const [responseWindow, setResponseWindow] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [remainingSeconds, setRemainingSeconds] = useState(DEFAULT_MANUAL_ALLOCATION_TIMEOUT_SEC);
  const [bookingSnapshot, setBookingSnapshot] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const stoppedRef = useRef(false);
  const startTimeRef = useRef(null);
  const advancingRef = useRef(false); // guards against advancing twice on overlapping poll ticks

  useEffect(() => {
    getTimeoutConfig().then((cfg) => {
      if (cfg?.manual_allocation_timeout_sec) {
        setResponseWindow(cfg.manual_allocation_timeout_sec);
      }
    });
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    let pollTimeoutId;
    let tickIntervalId;

    const resetCountdown = () => {
      startTimeRef.current = Date.now();
      setRemainingSeconds(responseWindow);
    };

    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const result = await commandCenterService.getBooking(bookingId);
        const booking = result.data;
        if (stoppedRef.current) return;
        setBookingSnapshot(booking);

        if (booking.status === 'CONFIRMED') {
          setPhase('confirmed');
          onConfirmed?.(booking);
          return;
        }

        if (booking.status === 'PENDING' && !booking.driver_id && !advancingRef.current) {
          advancingRef.current = true;
          try {
            const advanceResult = await commandCenterService.advanceAutoAssign(bookingId);
            if (stoppedRef.current) return;
            const { exhausted, currentAttempt, totalAttempts } = advanceResult.data;
            if (exhausted) {
              setPhase('exhausted');
              onExhausted?.();
              return;
            }
            setAttempt({ current: currentAttempt, total: totalAttempts });
            resetCountdown();
          } finally {
            advancingRef.current = false;
          }
        }
      } catch {
        // Transient fetch error — just try again on the next tick.
      }
      pollTimeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    const start = async () => {
      try {
        const result = await commandCenterService.startAutoAssign(bookingId, filters);
        if (stoppedRef.current) return;
        setAttempt({ current: result.data.currentAttempt, total: result.data.totalAttempts });
        setPhase('waiting');
        resetCountdown();
        tickIntervalId = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setRemainingSeconds(Math.max(0, responseWindow - elapsed));
        }, 1000);
        poll();
      } catch (error) {
        if (stoppedRef.current) return;
        const message = error?.message || 'Failed to start Auto Assign';
        if (message.toLowerCase().includes('no available drivers')) {
          setPhase('no-candidates');
        } else {
          setErrorMessage(message);
          setPhase('error');
        }
      }
    };

    start();

    return () => {
      stoppedRef.current = true;
      clearTimeout(pollTimeoutId);
      clearInterval(tickIntervalId);
    };
    // eslint-disable-next-line
  }, [bookingId]);

  // Same real cancellation as the single-driver waiting popup — closing
  // mid-attempt must actually stop the live request server-side, not just
  // stop this popup's own polling, otherwise the currently-contacted driver
  // could still accept after the dispatcher walked away. stoppedRef is set
  // FIRST, synchronously, before the async cancel call even goes out —
  // otherwise a poll tick already in flight could see the driver_id the
  // cancel call just cleared and call advanceAutoAssign, re-requesting a new
  // driver a moment after the dispatcher explicitly cancelled.
  const handleCancelClick = async () => {
    const wasWaiting = phase === 'waiting';
    const wasConfirmed = phase === 'confirmed';
    stoppedRef.current = true;
    if (wasWaiting) {
      try {
        await commandCenterService.cancelPendingRequest(bookingId);
      } catch {
        // Best-effort — still close locally either way.
      }
    }
    onClose();
    // "Done" (confirmed phase) also takes the dispatcher to the Trips page,
    // opened on whichever tab this booking actually landed in.
    if (wasConfirmed) onDone?.(deriveTripStage(bookingSnapshot));
  };

  const fare = bookingSnapshot?.total_price ?? bookingSnapshot?.estimated_fare ?? bookingSnapshot?.platform_service_fee ?? bookingSnapshot?.agreed_price ?? bookingSnapshot?.system_price;
  const driver = bookingSnapshot?.driver;
  const vehicle = bookingSnapshot?.vehicle;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleCancelClick}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-primary/5 p-4 border-b border-primary/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-1.5 rounded-full">
                <Truck className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {phase === 'confirmed' ? 'Driver Confirmed' : phase === 'exhausted' || phase === 'no-candidates' || phase === 'error' ? 'Auto Assign' : 'Contacting Driver'}
                </h3>
                <p className="text-xs text-gray-500">
                  {phase === 'starting'
                    ? 'Finding nearby drivers…'
                    : phase === 'waiting'
                    ? 'Waiting for driver to accept the request'
                    : phase === 'confirmed'
                    ? 'The driver accepted the booking request'
                    : phase === 'exhausted'
                    ? 'No nearby driver accepted'
                    : phase === 'no-candidates'
                    ? 'No available ambulances found'
                    : 'Something went wrong'}
                </p>
              </div>
            </div>
            <button onClick={handleCancelClick} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {phase === 'starting' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-600">Finding nearby drivers&hellip;</p>
            </div>
          )}

          {phase === 'waiting' && (
            <>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-center gap-2">
                <Clock className="w-5 h-5 text-primary animate-pulse" />
                <span className="text-lg font-bold text-gray-900 font-mono">{remainingSeconds}s</span>
                <span className="text-sm text-gray-500">
                  {remainingSeconds > 0 ? 'remaining to decide' : 'moving to next ambulance…'}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-gray-600" />
                  Ambulance Details
                </h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-500">Type:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{vehicle?.type?.name || '—'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Estimated Fare:</span>
                    <span className="text-sm font-bold text-gray-900 ml-2">{fare != null ? `₹${Math.round(fare)}` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
                <h4 className="text-xs font-semibold text-primary mb-3">Driver Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    <div>
                      <span className="text-xs text-primary/80">Driver Name:</span>
                      <span className="text-sm font-medium text-gray-900 ml-2">{driver?.name || '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <div>
                      <span className="text-xs text-primary/80">Mobile Number:</span>
                      <span className="text-sm font-medium text-gray-900 ml-2">{driver?.phone || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-primary" />
                    <div>
                      <span className="text-xs text-primary/80">Ambulance Number:</span>
                      <span className="text-sm font-medium text-gray-900 ml-2">{vehicle?.plate_number || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-gray-600" />
                  Vehicle Information <span className="text-green-600 font-normal">(Live)</span>
                </h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-500">Vehicle Number:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{vehicle?.plate_number || '—'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Vehicle Type:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">{vehicle?.type?.name || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <h4 className="text-xs font-semibold text-purple-700 mb-3">
                  Allocation Status <span className="text-green-600 font-normal">(Live)</span>
                </h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-purple-500">Status:</span>
                    <span className="text-sm font-medium text-purple-900 ml-2">WAITING</span>
                  </div>
                  <div>
                    <span className="text-xs text-purple-500">Driver Response:</span>
                    <span className="text-sm font-medium text-purple-900 ml-2">PENDING</span>
                  </div>
                  <div>
                    <span className="text-xs text-purple-500">Allocation Type:</span>
                    <span className="text-sm font-medium text-purple-900 ml-2">AUTOMATIC</span>
                  </div>
                  <div>
                    <span className="text-xs text-purple-500">Attempt:</span>
                    <span className="text-sm font-medium text-purple-900 ml-2">{attempt.current} of {attempt.total}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {phase === 'confirmed' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-green-900 mb-1">Booking Confirmed!</h3>
              <p className="text-sm text-green-700 mb-5">
                Driver {attempt.current} of {attempt.total} accepted the booking request.
              </p>

              <div className="space-y-3 text-left">
                {bookingSnapshot?.booking_id && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold mb-0.5">Booking ID</p>
                    <p className="font-mono tracking-wider text-blue-900 font-semibold">{bookingSnapshot.booking_id}</p>
                  </div>
                )}

                {(bookingSnapshot?.pickup_address || bookingSnapshot?.dropoff_address) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Trip Route</p>
                    <div className="flex items-start gap-2 mb-1.5">
                      <MapPin className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{bookingSnapshot?.pickup_address || 'Pickup location'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{bookingSnapshot?.dropoff_address || 'Drop-off location'}</span>
                    </div>
                  </div>
                )}

                <div className="bg-green-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Driver Details</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Driver</span>
                    <span className="font-medium text-gray-900">{driver?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Vehicle</span>
                    <span className="font-medium text-gray-900">{vehicle?.plate_number} &middot; {vehicle?.type?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Phone</span>
                    <span className="font-medium text-gray-900 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {driver?.phone}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 p-2.5 bg-green-100 rounded-lg mt-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">Driver Confirmed</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {phase === 'exhausted' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-red-900 mb-1">No Drivers Available</h3>
              <p className="text-sm text-red-700 mb-2">
                Tried {attempt.total} nearby driver{attempt.total === 1 ? '' : 's'} — none accepted in time.
              </p>
              <p className="text-xs text-gray-500 mt-3">
                Go back to the driver list to pick someone manually, or try Add &amp; Assign for an off-platform vehicle.
              </p>
            </div>
          )}

          {phase === 'no-candidates' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-red-900 mb-1">No Drivers Found</h3>
              <p className="text-sm text-red-700">No available ambulances were found nearby for this booking.</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-red-900 mb-1">Couldn't Start Auto Assign</h3>
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={handleCancelClick}
            className={`py-2 px-4 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${
              phase === 'confirmed'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : phase === 'waiting' || phase === 'starting'
                ? 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {phase === 'confirmed' ? 'Done' : phase === 'waiting' || phase === 'starting' ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommandCenterAutoAssignPopup;
